import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Truck, 
  Clock, 
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Calendar,
  Package,
  XCircle,
  RotateCcw
} from "lucide-react";
import { format, isToday, parseISO } from "date-fns";
import { PickupCompletionManager } from "@/components/admin/PickupCompletionManager";

interface TodayPickup {
  id: string;
  proposed_date: string;
  proposed_time_slot: string;
  status: string;
  address: string;
  user: {
    id: string;
    fullName: string;
    email: string;
  };
  packageCount: number;
}

interface FailedPickup {
  id: string;
  proposed_date: string;
  status: string;
  cancelled_reason?: string;
  user: {
    fullName: string;
    email: string;
  };
}

interface LogisticsStats {
  todayPickups: number;
  confirmedPickups: number;
  failedPickups: number;
  completedThisMonth: number;
}

export const LogisticsDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<LogisticsStats>({
    todayPickups: 0,
    confirmedPickups: 0,
    failedPickups: 0,
    completedThisMonth: 0,
  });
  const [todayPickups, setTodayPickups] = useState<TodayPickup[]>([]);
  const [failedPickups, setFailedPickups] = useState<FailedPickup[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLogisticsData = async () => {
    try {
      // Get pickup stats using RPC
      const { data: pickupStats } = await (supabase as any).rpc("get_pickup_stats");

      // Fetch today's confirmed pickups
      const today = format(new Date(), "yyyy-MM-dd");
      const { data: todayData } = await (supabase as any)
        .from("user_pickups")
        .select(`
          id,
          proposed_date,
          proposed_time_slot,
          status,
          address,
          user_id,
          profiles!user_pickups_user_id_fkey (id, email, first_name, last_name)
        `)
        .eq("proposed_date", today)
        .eq("status", "confirmed")
        .order("proposed_time_slot", { ascending: true });

      // Get package counts for each pickup
      const pickupsWithCounts: TodayPickup[] = await Promise.all(
        (todayData || []).map(async (pickup: any) => {
          const { count } = await (supabase as any)
            .from("packages")
            .select("*", { count: "exact", head: true })
            .eq("pickup_id", pickup.id);

          const firstName = pickup.profiles?.first_name || "";
          const lastName = pickup.profiles?.last_name || "";
          const fullName = firstName && lastName
            ? `${firstName} ${lastName}`
            : pickup.profiles?.email?.split("@")[0] || "Unknown";

          return {
            id: pickup.id,
            proposed_date: pickup.proposed_date,
            proposed_time_slot: pickup.proposed_time_slot,
            status: pickup.status,
            address: pickup.address,
            user: {
              id: pickup.user_id,
              fullName,
              email: pickup.profiles?.email || "",
            },
            packageCount: count || 0,
          };
        })
      );

      // Fetch failed pickups (missed, no_show) for rescheduling
      const { data: failedData } = await (supabase as any)
        .from("user_pickups")
        .select(`
          id,
          proposed_date,
          status,
          cancelled_reason,
          profiles!user_pickups_user_id_fkey (email, first_name, last_name)
        `)
        .in("status", ["missed", "no_show"])
        .order("proposed_date", { ascending: false })
        .limit(10);

      const failedList: FailedPickup[] = (failedData || []).map((pickup: any) => {
        const firstName = pickup.profiles?.first_name || "";
        const lastName = pickup.profiles?.last_name || "";
        const fullName = firstName && lastName
          ? `${firstName} ${lastName}`
          : pickup.profiles?.email?.split("@")[0] || "Unknown";

        return {
          id: pickup.id,
          proposed_date: pickup.proposed_date,
          status: pickup.status,
          cancelled_reason: pickup.cancelled_reason,
          user: {
            fullName,
            email: pickup.profiles?.email || "",
          },
        };
      });

      setStats({
        todayPickups: pickupStats?.today_pickups || 0,
        confirmedPickups: pickupStats?.confirmed_pickups || 0,
        failedPickups: failedList.length,
        completedThisMonth: pickupStats?.completed_this_month || 0,
      });

      setTodayPickups(pickupsWithCounts);
      setFailedPickups(failedList);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching logistics data:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogisticsData();

    // Subscribe to realtime updates
    const channel = supabase
      .channel("logistics-dashboard")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_pickups",
        },
        () => {
          fetchLogisticsData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getTimeSlotLabel = (slot: string) => {
    const slots: Record<string, string> = {
      "9am-12pm": "Morning (9 AM - 12 PM)",
      "12pm-3pm": "Afternoon (12 PM - 3 PM)",
      "3pm-6pm": "Evening (3 PM - 6 PM)",
    };
    return slots[slot] || slot;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "missed":
        return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">Missed</Badge>;
      case "no_show":
        return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">No-Show</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const statCards = [
    {
      title: "Today's Pickups",
      value: stats.todayPickups,
      icon: Calendar,
      color: "text-purple-500",
      bgColor: "bg-purple-50",
    },
    {
      title: "Confirmed Pickups",
      value: stats.confirmedPickups,
      icon: Truck,
      color: "text-blue-500",
      bgColor: "bg-blue-50",
    },
    {
      title: "Failed Pickups",
      value: stats.failedPickups,
      icon: AlertTriangle,
      color: "text-red-500",
      bgColor: "bg-red-50",
    },
    {
      title: "Completed This Month",
      value: stats.completedThisMonth,
      icon: CheckCircle2,
      color: "text-emerald-500",
      bgColor: "bg-emerald-50",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Logistics Dashboard</h1>
        <p className="text-muted-foreground">Daily pickup execution and management</p>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title} className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {loading ? "..." : card.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Today's Pickups */}
      <Card className="shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-purple-50">
              <Calendar className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Today's Pickups</CardTitle>
              <p className="text-muted-foreground text-sm">Scheduled for {format(new Date(), "MMMM d, yyyy")}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/admin/tasks")}>
            View All <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : todayPickups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No pickups scheduled for today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {todayPickups.map((pickup) => (
                <div
                  key={pickup.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Clock className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">{pickup.user.fullName}</p>
                      <p className="text-sm text-muted-foreground">{getTimeSlotLabel(pickup.proposed_time_slot)}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">{pickup.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span>{pickup.packageCount} packages</span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Failed Pickups Requiring Action */}
      {failedPickups.length > 0 && (
        <Card className="shadow-sm border-amber-200">
          <CardHeader className="flex flex-row items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Failed Pickups</CardTitle>
              <p className="text-muted-foreground text-sm">Require rescheduling or follow-up</p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {failedPickups.map((pickup) => (
                <div
                  key={pickup.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-4">
                    {pickup.status === "no_show" ? (
                      <div className="p-2 rounded-lg bg-red-50">
                        <XCircle className="h-5 w-5 text-red-500" />
                      </div>
                    ) : (
                      <div className="p-2 rounded-lg bg-amber-50">
                        <RotateCcw className="h-5 w-5 text-amber-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{pickup.user.fullName}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(parseISO(pickup.proposed_date), "MMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(pickup.status)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pickup Completion Manager for active pickups */}
      <PickupCompletionManager />
    </div>
  );
};
